import { AsyncPipe, NgIf } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostBinding,
  Injector,
  Input,
  Output,
  Signal,
  ViewEncapsulation,
  computed,
  inject,
  runInInjectionContext
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatFormFieldModule } from '@angular/material/form-field';
import { ThemeAssetComponent } from '@partners/themes';
import { isEmpty } from '@partners/utils';
import { debounceTime, startWith } from 'rxjs/operators';
import { ExtendedFormControl } from '../../controls/control';
import { IFormField } from '../../model';

/**
 * @description
 * a wrapper component which toggles a form field's reset button
 */

@Component({
  standalone: true,
  selector: 'field-reset',
  template: `
    @if(enableReset$$()) {
    <button
      tabindex="-1"
      mat-flat-button
      type="button"
      aria-label="נקה שדה"
      (click)="resetValue($event)"
      style="max-width: 22px;opacity:.65"
    >
      <theme-asset asset="reset-icon"></theme-asset>
    </button>
    }
  `,
  imports: [NgIf, AsyncPipe, ThemeAssetComponent, MatFormFieldModule],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FieldResetComponent<T> implements AfterViewInit {
  @Input()
  set fieldConfig(formField: IFormField<T>) {
    /** we need access to the form control's instance in order to reset its value (see resetValue() below) */
    this.#control = formField.control;

    runInInjectionContext(this.#localInjector, () => {
      this.#controlValue$$ = toSignal(this.#control.valueChanges.pipe(startWith(undefined), debounceTime(150)));

      /** detect when the reset functionality is accessible to the user */
      this.enableReset$$ = computed<boolean>(() => {
        /**
         * running out of context makes angular miss control's value on load event
         * to compensate for a control with value on load - we also check the value
         * of the formField.control
         */
        const value = this.#controlValue$$() || this.#control.value;
        const { control } = formField;
        const { disabled } = control;

        if (isEmpty(value) && !disabled) {
          return false;
        }
        return control.enabled;
      });
    });
  }

  @Output()
  reset = new EventEmitter<boolean>();

  @HostBinding('class')
  get hostClass() {
    return 'field-reset';
  }

  enableReset$$: Signal<boolean>;

  #localInjector = inject(Injector);
  #controlValue$$: Signal<T>;
  #changeDetector = inject(ChangeDetectorRef);
  #control: ExtendedFormControl<T>;

  constructor() {}
  ngAfterViewInit(): void {
    this.#changeDetector.detectChanges();
  }

  /**
   * @description
   * reset the form control by deleting its value
   * @param event a click event handler
   */
  resetValue(event: MouseEvent) {
    event.stopPropagation();
    event.preventDefault();
    this.#control.setValue(undefined, { onlySelf: true, emitEvent: true });
    this.#control.updateValueAndValidity({ onlySelf: true, emitEvent: true });
    this.#changeDetector.markForCheck();
    this.reset.emit(true);
  }
}
